import React, { useState, useEffect, useRef } from 'react';
import './HeroSection.css';
import backgroundImage from '../assets/backg.webp';

const HeroSection = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    // Preload the hero image with high priority
    const img = new Image();
    img.src = backgroundImage;
    img.fetchPriority = 'high'; // Modern browsers
    img.decoding = 'async'; // Don't block rendering
    img.onload = () => {
      setImageLoaded(true);
      // If we have a ref to an img element, mark it as loaded
      if (imageRef.current) {
        imageRef.current.loading = 'eager';
      }
    };
    img.onerror = () => {
      console.error('Failed to load hero background image');
      setImageLoaded(true); // Show content anyway
    };

    // Also preconnect to Cloudinary if using external images
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://res.cloudinary.com';
    document.head.appendChild(preconnect);

    return () => {
      if (preconnect.parentNode) {
        document.head.removeChild(preconnect);
      }
    };
  }, []);

  // Structured Data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "CBC Center",
    "alternateName": "Competency Based Curriculum Center",
    "description": "Your Gateway to Quality Education Resources - Access comprehensive Competency Based Curriculum materials for Grade 6, 9, 10, 11, and 12",
    "url": "https://cbccenter.online",
    "logo": "https://cbccenter.online/logo.png",
    "sameAs": [
      "https://facebook.com/cbccenter",
      "https://twitter.com/cbccenter",
      "https://linkedin.com/company/cbccenter"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "KE"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://cbccenter.online/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <>
      {/* Structured Data for Google */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Hidden preload for LCP image - Critical for performance */}
      <link 
        rel="preload" 
        as="image" 
        href={backgroundImage}
        imagesrcset={`${backgroundImage} 1200w, ${backgroundImage} 800w`}
        imagesizes="100vw"
        fetchpriority="high"
        onLoad={() => console.log('Hero image preloaded')}
      />
      
      <section className="hero-section" aria-label="CBC Center Hero Section">
        {/* OPTION 1: Using background-image (current setup) */}
        <div 
          className={`hero-image ${imageLoaded ? 'loaded' : 'loading'}`}
          style={{ 
            backgroundImage: imageLoaded ? `url(${backgroundImage})` : 'none',
            // Add inline styles for immediate rendering
            backgroundColor: imageLoaded ? 'transparent' : '#f8f9fa'
          }}
          aria-hidden="true"
        >
          {/* OPTION 2: Hidden img tag for better LCP detection (recommended) */}
          <img
            ref={imageRef}
            src={backgroundImage}
            alt=""
            loading="eager"
            fetchPriority="high"
            width="1920"
            height="1080"
            style={{
              display: 'none',
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0
            }}
            onLoad={() => setImageLoaded(true)}
          />
        </div>
        
        <div className="hero-content-container">
          <div className="hero-content">
            <h1 className="hero-title" id="main-heading">
              CBC Center
            </h1>
            
            <h2 className="hero-subtitle">
              Your Gateway to Quality Education Resources
            </h2>
            
            {/* Add Breadcrumb Schema */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [{
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://cbccenter.online"
                }]
              })}
            </script>
            
            <div className="hero-stats">
              <div className="stat-item" itemScope itemType="https://schema.org/Offer">
                <div className="stat-content">
                  <span className="stat-number" itemProp="price">50+</span>
                  <span className="stat-label" itemProp="name">Learning Materials</span>
                </div>
              </div>
              
              <div className="stat-item" itemScope itemType="https://schema.org/Offer">
                <div className="stat-content">
                  <span className="stat-number" itemProp="price">100%</span>
                  <span className="stat-label" itemProp="name">Curriculum Aligned</span>
                </div>
              </div>
              
              <div className="stat-item" itemScope itemType="https://schema.org/Offer">
                <div className="stat-content">
                  <span className="stat-number" itemProp="price">Free</span>
                  <span className="stat-label" itemProp="name">Always Free Access</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;